import { isEmpty } from 'lodash';

function compareValues(comparison, conditionalValues, value) {
  switch (comparison) {
    case 'IS':
      return conditionalValues.length === 1 && conditionalValues[0] === value;
    case 'IS_NOT':
      return conditionalValues.length > 0 && conditionalValues.every(_value => _value !== value);
    case 'IS_EMPTY':
      return conditionalValues.length === 0;
    case 'IS_NOT_EMPTY':
      return conditionalValues.length > 0;
    case 'CONTAINS':
      return conditionalValues.includes(value);
    case 'DOES_NOT_CONTAIN':
      return !isEmpty(conditionalValues) && !conditionalValues.includes(value);
  }
}

/**
 * Returns true if the variant should be shown, false if it should be hidden
 * @param {Object} variant - the variant to check
 * @param {Object} formVariants - the variants object from the form
 * @param {Object} productVariants - the variants object from the product
 * @param {Object} query - the query object which may contain `show_variants` as a comma separated list of variant references to show
 * @returns {Boolean}
 */
export default function checkIsVariantEnabled(
  variant,
  formVariants,
  productVariants = [],
  visited = new Set(),
) {
  // if showHideLogic is not set, return the variant
  if (!variant.showHideLogic) return true;
  if (!variant.showHideLogic.enabled) return true;

  const { element, comparison, value } = variant.showHideLogic;

  const comparisonElement = productVariants.find(variant => variant._id === element.elementId);

  if (!comparisonElement) {
    return false;
  }

  const formVariantOptions = formVariants[element.elementId] || [];

  /**
   * Ensure that formVariants not only has the variant in its array but has a quantity.
   * A selected variant with 0 quantity is treated as unselected/empty.
   */
  const validOptions = formVariantOptions.filter(option => option?.quantity > 0);

  // check for circular dependencies
  if (visited.has(variant._id)) {
    return false;
  }

  visited.add(variant._id);

  /**
   * We allow the user to setup two variants both with IS_EMPTY that depend on each other.
   * In that case we want to skip the checkIsVariantEnabled because neither will be visible on its own.
   */
  if (comparison === 'IS_EMPTY') {
    const conditionalValues = validOptions.map(option => option.optionId);
    return compareValues(comparison, conditionalValues, value);
  }

  const isComparisonElementVisible = checkIsVariantEnabled(
    comparisonElement,
    formVariants,
    productVariants,
    visited,
  );
  if (!isComparisonElementVisible) return false;

  const conditionalValues = validOptions.map(option => option.optionId);
  return compareValues(comparison, conditionalValues, value);
}
